import {
  type ConfigResourceEditSchemaType,
  configResourceEditSchema,
} from '@/forms/configResource';
import { useUpdateCashVaultBranchMutation } from '@/store/api/services/operations/cashVaultBranches';
import type { CashVaultBranch } from '@/types/configs';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface EditCashVaultBranchProps extends ModalChildrenProps {
  props: {
    cashVaultBranch: CashVaultBranch;
  } | null;
}

const EditCashVaultBranch = ({ hide, props }: EditCashVaultBranchProps) => {
  const cashVaultBranch = props?.cashVaultBranch;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ConfigResourceEditSchemaType>({
    resolver: zodResolver(configResourceEditSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: cashVaultBranch?.name || '',
      description: cashVaultBranch?.description || '',
      is_active: cashVaultBranch?.is_active ?? true,
    },
  });

  const [updateCashVaultBranch] = useUpdateCashVaultBranchMutation();

  const onSubmit = async (payload: ConfigResourceEditSchemaType) => {
    if (!cashVaultBranch?.id) {
      toast.error('Cash vault branch ID is missing');
      return;
    }

    try {
      await updateCashVaultBranch({ id: cashVaultBranch.id, ...payload }).unwrap();
      toast.success('Cash vault branch updated successfully');
      hide('edit-cash-vault-branch');
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to update cash vault branch';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          <TextField
            label="Name *"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            size="small"
            inputProps={{ 'data-test': 'config-name-input' }}
            {...register('name')}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch {...field} checked={field.value} data-test="config-active-switch" />
                }
                label="Active"
              />
            )}
          />

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Description *
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <>
                  <TextareaAutosize
                    {...field}
                    data-test="config-description-input"
                    minRows={4}
                    maxRows={8}
                    placeholder="Provide a detailed description of the cash vault branch"
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      padding: '12px 14px',
                      border: errors.description
                        ? '1px solid #d32f2f'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      backgroundColor: 'transparent',
                      outline: 'none',
                      cursor: 'text',
                    }}
                  />
                  {errors.description && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.description.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide('edit-cash-vault-branch')} data-test="config-cancel-btn">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          data-test="config-submit-btn"
        >
          {isSubmitting ? 'Updating...' : 'Update Cash Vault Branch'}
        </Button>
      </DialogActions>
    </>
  );
};

export default EditCashVaultBranch;
